import React, { useState, useEffect } from 'react';
import { AxiosInstanceWithCache } from '../../../components/utils';

interface Testimonial {
  id: string;
  name: string;
  photoUrl?: string;
  content: string;
  date: number;
  eventcode?: string;
  status: string;
}

const TestimonialsSection: React.FC = () => {
  const [testimonials, setTestimonials] = useState<Testimonial[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [modalContent, setModalContent] = useState('');
  const [modalAuthor, setModalAuthor] = useState('');
  const [modalEventcode, setModalEventcode] = useState<string | null>(null);

  useEffect(() => {
    const fetchTestimonials = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const response = await AxiosInstanceWithCache.get('/api/gettestimonials');
        
        if (response.data.result === 'Success') {
          setTestimonials(response.data.data);
        } else {
          setError('Failed to fetch testimonials');
        }
      } catch (err) {
        console.error('Error fetching testimonials:', err);
        setError('Failed to load testimonials. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchTestimonials();
  }, []);

  useEffect(() => {
    if (testimonials.length > 0) {
      const interval = setInterval(() => {
        setCurrentIndex((prev) => (prev + 1) % testimonials.length);
      }, 10000);
      return () => clearInterval(interval);
    }
  }, [testimonials]);

  const handleIndicatorClick = (index: number) => {
    setCurrentIndex(index);
  };

  if (loading) {
    return (
      <section className="testimonials" aria-label="Testimonials">
        <h3 className="section-title">Testimonials</h3>
        <div className="loading-message">
          Loading testimonials...
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="testimonials" aria-label="Testimonials">
        <h3 className="section-title">Testimonials</h3>
        <div className="error-message">
          {error}
        </div>
      </section>
    );
  }

  if (testimonials.length === 0) {
    return (
      <section className="testimonials" aria-label="Testimonials">
        <h3 className="section-title">Testimonials</h3>
        <div className="empty-message">
          No testimonials available at this time.
        </div>
      </section>
    );
  }

  const currentTestimonial = testimonials[currentIndex];

  // Truncate content if too long
  const truncateContent = (content: string, maxLength: number = 500) => {
    if (content.length <= maxLength) {
      return content;
    }
    return content.substring(0, maxLength) + '...';
  };

  const handleReadMore = () => {
    setModalContent(currentTestimonial.content);
    setModalAuthor(currentTestimonial.name);
    setModalEventcode(currentTestimonial.eventcode || null);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
  };

  return (
    <section className="testimonials" aria-label="Testimonials">
      <h3 className="section-title">Testimonials</h3>
      <div className="testimonial-content">
        <div className="testimonial-text-container">
          <blockquote className="testimonial-text" aria-live="polite" aria-atomic="true">
            {truncateContent(currentTestimonial.content)}
            {currentTestimonial.content.length > 300 && (
              <button 
                className="read-more-btn" 
                onClick={handleReadMore}
                aria-label="Read full testimonial"
              >
                more
              </button>
            )}
          </blockquote>
          <div className="testimonial-author-info">
            {currentTestimonial.photoUrl && (
              <img 
                src={currentTestimonial.photoUrl} 
                alt={currentTestimonial.name}
                className="testimonial-image"
              />
            )}
            <div>
              <cite className="testimonial-author">-{currentTestimonial.name}</cite>
              {currentTestimonial.eventcode && (
                <span className="testimonial-eventcode">
                  {currentTestimonial.eventcode}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
      <div className="testimonial-controls" role="tablist" aria-label="Testimonial navigation">
        {testimonials.map((_, index) => (
          <button
            key={index}
            aria-label={`Show testimonial ${index + 1}`}
            className={index === currentIndex ? 'active' : ''}
            onClick={() => handleIndicatorClick(index)}
          />
        ))}
      </div>

      {/* Modal for full content */}
      {showModal && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={handleCloseModal} aria-label="Close modal">
              ×
            </button>
            <div className="modal-header">
              {currentTestimonial.photoUrl && (
                <img 
                  src={currentTestimonial.photoUrl} 
                  alt={modalAuthor}
                  className="modal-author-image"
                />
              )}
              <div>
                <h4 className="modal-author">-{modalAuthor}</h4>
                {modalEventcode && (
                  <span className="modal-eventcode">{modalEventcode}</span>
                )}
              </div>
            </div>
            <div className="modal-body">
              <p>{modalContent}</p>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default TestimonialsSection;